import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, BehaviorSubject, tap, map, shareReplay } from 'rxjs';
import { HttpService } from '../http/http.service';

export interface UserPermissions {
  canCreateProjects: boolean;
  canManageUsers: boolean;
  canAccessAdminPanel: boolean;
  canManageRepositories: boolean;
  canExecutePipelines: boolean;
  role: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private httpService = inject(HttpService);
  // Cache para permissões
  private permissionsCache$: Observable<UserPermissions> | null = null;
  private permissionsSubject = new BehaviorSubject<UserPermissions | null>(null);

  isAuthenticated(): boolean {
    return !!this.getAuthorizationToken();
  }

  saveLocalStorage(user: any, isSaml?: boolean): Promise<any> {
    return new Promise((resolve) => {
      localStorage.setItem('idUser', isSaml ? user?.user._id : user?.user.id);
      localStorage.setItem('authToken', user.token);
      localStorage.setItem('name', user?.user.name);
      localStorage.setItem('role', user?.user.role);
      
      // Limpa cache de permissões para forçar nova verificação
      this.clearPermissionsCache();
      
      resolve(true);
    });
  }

  removeLocalStorage(): Promise<any> {
    return new Promise<any>((resolve) => {
      sessionStorage.clear();
      localStorage.clear();
      this.clearPermissionsCache(); // Limpa cache de permissões
      resolve(true);
    });
  }

  getAuthorizationToken() {
    const token = localStorage.getItem('authToken');
    return token;
  }

  getUserRole(): string | null {
    return localStorage.getItem('role');
  }

  isAdmin(): boolean {
    const role = this.getUserRole();
    return role === 'admin';
  }

  isUser(): boolean {
    const role = this.getUserRole();
    return role === 'user';
  }

  canCreateProjects(): boolean {
    return this.isAdmin() || this.isProjectManager();
  }

  canManageUsers(): boolean {
    return this.isAdmin();
  }

  canAccessAdminPanel(): boolean {
    return this.isAdmin();
  }

  canManageRepositories(): boolean {
    return this.isAdmin() || this.isDeveloper() || this.isProjectManager();
  }

  canExecutePipelines(): boolean {
    return this.isAdmin() || this.isDeveloper() || this.isDevOps() || this.isQA();
  }

  isProjectManager(): boolean {
    return this.getUserRole() === 'project_manager';
  }

  isDeveloper(): boolean {
    const role = this.getUserRole();
    return role === 'developer' || role === 'senior_developer' || role === 'junior_developer';
  }

  isDevOps(): boolean {
    return this.getUserRole() === 'devops_engineer';
  }

  isQA(): boolean {
    return this.getUserRole() === 'qa_engineer';
  }

  isClientManager(): boolean {
    return this.getUserRole() === 'client_manager';
  }

  isAuditor(): boolean {
    return this.getUserRole() === 'auditor';
  }

  // Método melhorado com cache e configuração
  getUserPermissions(): Observable<UserPermissions> {
    // Se já temos cache, retorna ele
    if (this.permissionsCache$) {
      return this.permissionsCache$;
    }

    const token = this.getAuthorizationToken();
    if (!token) {
      return of({ 
        canCreateProjects: false, 
        canManageUsers: false,
        canAccessAdminPanel: false,
        canManageRepositories: false,
        canExecutePipelines: false,
        role: 'user' 
      });
    }

    // Cria o cache com shareReplay para reutilizar a requisição
    this.permissionsCache$ = this.http.get<UserPermissions>('/users/me/permissions', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).pipe(
      tap(permissions => this.permissionsSubject.next(permissions)),
      catchError(() => {
        // Fallback para localStorage em caso de erro
        console.warn('Erro ao verificar permissões via backend, usando localStorage como fallback');
        const fallbackPermissions = { 
          canCreateProjects: this.canCreateProjects(),
          canManageUsers: this.canManageUsers(),
          canAccessAdminPanel: this.canAccessAdminPanel(),
          canManageRepositories: this.canManageRepositories(),
          canExecutePipelines: this.canExecutePipelines(),
          role: this.getUserRole() || 'user' 
        };
        this.permissionsSubject.next(fallbackPermissions);
        return of(fallbackPermissions);
      }),
      shareReplay(1) // Cache o resultado
    );

    return this.permissionsCache$;
  }

  // Método síncrono para verificar se pode criar projetos (usa cache)
  canCreateProjectsSync(): boolean {
    const cachedPermissions = this.permissionsSubject.value;
    return cachedPermissions?.canCreateProjects || false;
  }

  // Método assíncrono para verificar permissões (carrega do backend se necessário)
  canCreateProjectsAsync(): Observable<boolean> {
    return this.getUserPermissions().pipe(
      map(permissions => permissions.canCreateProjects)
    );
  }

  // Limpa o cache quando o usuário faz logout
  clearPermissionsCache(): void {
    this.permissionsCache$ = null;
    this.permissionsSubject.next(null);
  }

  // Força refresh das permissões (útil após mudanças de role)
  refreshPermissions(): Observable<UserPermissions> {
    this.clearPermissionsCache();
    return this.getUserPermissions();
  }

  getAuthUser(): Observable<any> {
    return this.httpService.genericGet('auth/saml', '')
      .pipe((res) => {
        return res;
      });
  }
}
